import * as Sentry from "@sentry/react";

// Default is "" (relative) — the app is served same-origin behind Caddy on the
// VPS, so /api and /assets resolve to the current host. The VITE_API_URL* vars
// are optional overrides for when the API is on a different origin.
export const API_BASE =
  (import.meta.env.VITE_API_URL_PROD as string | undefined) ??
  (import.meta.env.VITE_API_URL_SANDBOX as string | undefined) ??
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "";
const TOKEN_KEY = "sa_token";

export function saveToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 401) {
    // Only force-redirect to login if there was a token (session expired).
    // If no token exists this is an expected unauthenticated check (e.g. public
    // pages calling getMe to detect auth state) — just throw, don't redirect.
    if (token) {
      clearToken();
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `Request failed: ${res.status}`);
    if (res.status >= 500) {
      Sentry.captureException(error, {
        tags: { api_path: path, http_status: String(res.status) },
        extra: { responseBody: text },
      });
    }
    throw error;
  }

  return (await res.json()) as T;
}
